import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Select from '../../juristec-ui/core/SelectNew';
import Button from '../../juristec-ui/core/Button';
import { Close, Reload } from '../../juristec-ui/icons';
import {
  SelectContainer,
  SelectedWrapper,
} from '../../juristec-ui/core/SelectNew/styled/Select.styled';

const DATE_PARTS = [
  { key: 'year', label: 'Ano' },
  { key: 'semester', label: 'Semestre' },
  { key: 'quarter', label: 'Trimestre' },
  { key: 'month', label: 'Mês' },
  { key: 'week', label: 'Semana ISO' },
  { key: 'day', label: 'Dia do mês' },
  { key: 'weekday', label: 'Dia da semana' },
  { key: 'hour', label: 'Hora' },
  { key: 'minute', label: 'Minuto' },
  { key: 'second', label: 'Segundo' },
];

function axisDateFields(campos) {
  const existing = new Set(campos.map((campo) => campo.key));
  return campos.flatMap((campo) => {
    const type = String(campo.tipo || '').toLowerCase();
    const isDate = type === 'date' || type === 'datetime' || type.startsWith('datetime64');
    if (!isDate) return [campo];

    return [
      { ...campo, label: `${campo.label} — Data completa` },
      ...DATE_PARTS.filter((part) => !existing.has(`${campo.key}::${part.key}`))
        .map((part) => ({
          ...campo,
          key: `${campo.key}::${part.key}`,
          label: `${campo.label} — ${part.label}`,
        })),
    ];
  });
}

const FormFields = styled.fieldset`
  && {
    --file-accent: ${({ theme }) => theme.primary || '#5602c1'};
    --file-focus: ${({ theme }) => theme.warning || '#e5b63c'};
    color: ${({ theme }) => theme.color || '#212121'};
    background: ${({ theme }) => theme.modal || '#fff'};
    min-width: 0;
  }

  && legend { color: var(--file-accent); }
  && label { color: inherit; }
  && > label { margin-bottom: 16px; }
  && ${SelectContainer} {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    padding-top: 0;
    overflow: visible;
  }
  && ${SelectContainer} > .labelTxt {
    position: static;
    display: block;
    grid-row: 1;
    grid-column: 1;
    margin: 0 0 8px;
    line-height: 1.4;
    white-space: normal;
  }
  && ${SelectedWrapper} {
    grid-row: 2;
    grid-column: 1;
  }
  && select {
    color: inherit;
    background-color: ${({ theme }) => theme.container || '#fafafa'};
    border: 1px solid var(--file-accent);
    border-radius: 8px;
    min-height: 40px;
    font: inherit;
  }
  && select:hover:not(:disabled) { border-color: var(--file-accent); }
  && input[type='checkbox'] {
    accent-color: var(--file-accent);
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    cursor: pointer;
  }
  && .file-axis-move {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 1px solid var(--file-accent);
    border-radius: 8px;
    color: var(--file-accent);
    background: transparent;
    font: inherit;
    font-size: 18px;
    cursor: pointer;
  }
  && .file-axis-move:hover:not(:disabled) {
    color: #fff;
    background: var(--file-accent);
  }
  && .file-axis-move > svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
    pointer-events: none;
  }
  && .file-axis-move:disabled,
  && fieldset:disabled .file-axis-move,
  && select:disabled { opacity: .45; cursor: default; }
  && .file-axis-move:focus-visible,
  && select:focus-visible,
  && input:focus-visible {
    outline: 3px solid var(--file-focus);
    outline-offset: 2px;
  }
  && .file-axis-row {
    padding: 6px 8px;
    border-bottom: 1px solid ${({ theme }) => theme.containerLine || '#ddd'};
    gap: 8px;
  }
  && .file-axis-row label { cursor: pointer; flex-direction: row; }
  && .file-axis-row:hover { background: ${({ theme }) => theme.containerHover || '#f5f5f5'}; }
  && p[role='alert'] { color: ${({ theme }) => theme.error || '#ba000d'}; }
`;

function AxisFields({ label, campos, selected, onChange, disabled }) {
  const move = (index, offset) => {
    const next = [...selected];
    const target = index + offset;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <fieldset disabled={disabled} style={{ minWidth: 0 }}>
      <legend>{label}</legend>

      <Select
        label={`Selecionar campos de ${label.toLowerCase()}`}
        placeholder="Selecione os campos"
        multiple
        searchable
        clearable
        stayOpen
        atModal
        disabled={disabled}
        options={[...campos].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', {
          sensitivity: 'base',
          numeric: true,
        })).map((campo) => ({
          id: `${label}-${campo.key}`,
          value: campo.key,
          label: campo.label,
        }))}
        value={selected.map((key) => ({
          id: `${label}-${key}`,
          value: key,
          label: campos.find((campo) => campo.key === key)?.label || key,
        }))}
        onChange={(options) => {
          const keys = options.map((option) => option.value);
          // Preserva a ordem manual e acrescenta as novas seleções ao final.
          onChange([
            ...selected.filter((key) => keys.includes(key)),
            ...keys.filter((key) => !selected.includes(key)),
          ]);
        }}
      />

      {selected.length > 0 && (
        <ol aria-label={`Ordem das categorias de ${label.toLowerCase()}`}
          style={{ paddingLeft: 24, marginTop: 12 }}>
          {selected.map((key, index) => {
            const name = campos.find((campo) => campo.key === key)?.label || key;
            return (
              <li key={key}>
                <div className="file-axis-row" style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ flex: 1 }}>{name}</span>
                  <button className="file-axis-move" type="button" disabled={disabled || index === 0}
                    aria-label={`Mover ${name} para cima em ${label}`}
                    onClick={() => move(index, -1)}>↑</button>
                  <button className="file-axis-move" type="button" disabled={disabled || index === selected.length - 1}
                    aria-label={`Mover ${name} para baixo em ${label}`}
                    onClick={() => move(index, 1)}>↓</button>
                  <button className="file-axis-move" type="button" disabled={disabled}
                    aria-label={`Remover ${name} de ${label}`}
                    title={`Remover ${name}`}
                    onClick={() => onChange(selected.filter((item) => item !== key))}>
                    <Close aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </fieldset>
  );
}

export default function FileConfigForm({
  value,
  onChange,
  fontes = [],
  carregarCampos,
  disabled = false,
  onDelete,
}) {
  const fonte = fontes.find((item) => item.id === value.fonteId);
  const [reloadVersion, setReloadVersion] = useState(0);
  const forceReload = useRef(false);

  const [resultado, setResultado] = useState({
    fonteId: '',
    campos: [],
    erro: '',
    carregando: false,
  });

  useEffect(() => {
    if (!value.fonteId) return undefined;

    let active = true;
    const fonteId = value.fonteId;
    const force = forceReload.current;
    forceReload.current = false;

    setResultado({
      fonteId,
      campos: [],
      erro: '',
      carregando: true,
    });

    Promise.resolve()
      .then(() => carregarCampos(fonteId, { force }))
      .then((campos) => {
        if (active) {
          setResultado({
            fonteId,
            campos,
            erro: '',
            carregando: false,
          });
        }
      })
      .catch((error) => {
        if (active) {
          setResultado({
            fonteId,
            campos: [],
            erro: error.message,
            carregando: false,
          });
        }
      });

    return () => {
      active = false;
    };
  }, [value.fonteId, carregarCampos, reloadVersion]);

  const campos = resultado.fonteId === value.fonteId
    ? resultado.campos
    : [];
  const camposDosEixos = axisDateFields(campos);

  const carregando = Boolean(
    value.fonteId
    && (
      resultado.fonteId !== value.fonteId
      || resultado.carregando
    )
  );

  const alterar = (changes) => {
    onChange({ ...value, ...changes });
  };

  return (
    <FormFields disabled={disabled}>
      <legend>Dados do relatório</legend>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 16 }}>
      <label style={{ flex: 1, minWidth: 0 }}>
        Fonte de dados
        <select
          value={value.fonteId}
          onChange={(event) => {
            // Campos da fonte anterior deixam de ser válidos.
            alterar({
              fonteId: event.target.value,
              valor: '',
              linhas: [],
              colunas: [],
              filtros: [],
              openingBalance: '',
            });
          }}
        >
          <option value="">Selecione uma fonte</option>

          {[...fontes].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', {
            sensitivity: 'base',
            numeric: true,
          })).map((item) => (
            <option key={item.id} value={item.id}>
              {item.nome}
            </option>
          ))}
        </select>
      </label>
      <button
        className="file-axis-move"
        type="button"
        disabled={disabled || !fonte || carregando}
        aria-label="Recarregar colunas do arquivo"
        title="Recarregar colunas do arquivo"
        onClick={() => {
          forceReload.current = true;
          setReloadVersion((version) => version + 1);
        }}
      >
        <Reload fill="currentColor" />
      </button>
      </div>

      <label>
        Campo de valor
        <select
          value={value.valor}
          disabled={!fonte || carregando}
          onChange={(event) => alterar({ valor: event.target.value })}
        >
          <option value="">Selecione um campo numérico</option>

          {campos
            .filter((campo) => campo.tipo === 'numero')
            .map((campo) => (
              <option key={campo.key} value={campo.key}>
                {campo.label}
              </option>
            ))}
        </select>
      </label>

      <label>
        Saldo inicial (opcional)

        <input
          type="number"
          step="0.01"
          placeholder="Ex.: 1000,00"
          value={value.openingBalance ?? ''}
          disabled={disabled}
          onChange={(event) => {
            alterar({ openingBalance: event.target.value });
          }}
        />
      </label>

      <p>
        Saldo anterior ao primeiro período. Deixe vazio para não calcular o saldo.
        {' '}Nas colunas, coloque as datas antes das categorias. O saldo aparece nos períodos; as categorias mostram “—”.
      </p>

      <AxisFields
        label="Linhas"
        campos={camposDosEixos}
        selected={value.linhas || []}
        disabled={disabled || !fonte || carregando || Boolean(resultado.erro)}
        onChange={(linhas) => alterar({ linhas })}
      />

      <AxisFields
        label="Colunas"
        campos={camposDosEixos}
        selected={value.colunas || []}
        disabled={disabled || !fonte || carregando || Boolean(resultado.erro)}
        onChange={(colunas) => alterar({ colunas })}
      />
        {carregando && <p role="status">Carregando campos…</p>}

        {resultado.fonteId === value.fonteId && resultado.erro && (
        <p role="alert">{resultado.erro}</p>
        )}
      <label className="check" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 20 }}>
        <input
          type="checkbox"
          checked={value.saveConfigurationOnly === true}
          disabled={disabled}
          onChange={(event) => alterar({ saveConfigurationOnly: event.target.checked })}
        />
        Salvar apenas as configurações
      </label>
      <p>Os dados serão consultados ao abrir o painel. Use esta opção para tabelas grandes.</p>
      {onDelete && (
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid currentColor' }}>
          <Button type="button" color="error" variant="outlined" disabled={disabled} onClick={onDelete}>
            Apagar explorador
          </Button>
        </div>
      )}
    </FormFields>
  );
};
